<template>
	<SettingsSection
		:loading="loading"
		title="Forms"
		class="org-settings-form-usage"
		loader-type="table"
		:loading-size="6"
		:row-height="50"
	>
		<template #button>
			<div>
				<CButton v-if="showOrganizationAuditLog" class="button button--secondary mr-1" @click="$emit('org-audit-log')">
					<template #icon>
						<Inline svg="audit-log" />
					</template>
					Audit Log
				</CButton>
				<CButton v-if="formsUsageData.length > 0" class="button button--secondary" @click="downloadDetails()">
					<template #icon>
						<Inline svg="download" />
					</template>
					Export All Details
				</CButton>
			</div>
		</template>

		<table class="table-style w-full relative">
			<thead class="sticky">
				<tr class="header-row" data-testid="table-header">
					<th class="text-left py-0 col-checkbox">
						<div class="flex flex-row items-center">
							<CCheckbox
								ref="selectAllCheckbox"
								title="Select All"
								aria-label="Select All"
								class="org-settings-form-usage__checkbox relative"
								:value="isSelectAll"
								@change="(selected) => selectAllToggle(selected)"
							/>
						</div>
					</th>
					<th class="text-left py-0 cursor-pointer" @click="changeSort(0)">
						<span class="relative top-1">
							Name
							<template v-if="sortColumn === 0">
								<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
								<Inline v-else svg="sort-down" class="text-icon-subtle" />
							</template>
						</span>
					</th>
					<th class="text-left py-0 b6:show cursor-pointer" @click="changeSort(1)">
						<span class="relative top-1">
							Folder
							<template v-if="sortColumn === 1">
								<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
								<Inline v-else svg="sort-down" class="text-icon-subtle" />
							</template>
						</span>
					</th>
					<th class="org-settings-form-usage__docs-header b9:show py-0 align-middle cursor-pointer" @click="changeSort(2)">
						<span class="relative top-1">
							Document Templates
							<template v-if="sortColumn === 2">
								<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
								<Inline v-else svg="sort-down" class="text-icon-subtle" />
							</template>
						</span>
					</th>
					<th class="b9:show py-0 cursor-pointer" @click="changeSort(3)">
						<span class="relative top-1">
							Storage
							<template v-if="sortColumn === 3">
								<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
								<Inline v-else svg="sort-down" class="text-icon-subtle" />
							</template>
						</span>
					</th>
					<th class="b7:show py-0 cursor-pointer" @click="changeSort(4)">
						<span class="relative top-1">
							Entries
							<template v-if="sortColumn === 4">
								<Inline v-if="sortAscending" svg="sort-up" class="text-icon-subtle" />
								<Inline v-else svg="sort-down" class="text-icon-subtle" />
							</template>
						</span>
					</th>
					<th class="col-actions"></th>
				</tr>
				<tr class="bulk-actions-row absolute top-0 left-0 right-0 bg-th" :class="{'hidden': !showBulkActionsBar}" data-testid="bulk-actions">
					<th colspan="7" class="p-0">
						<FormBulkActionsBar
							ref="formBulkActionsBar"
							class="bulk-actions-bar bg-th relative"
							:selected-forms="getSelectedForms()"
							:all-forms-selected="isSelectAll"
							:is-select-all-indeterminate="isSelectAllIndeterminate"
							@close="closeBulkActionsBar"
							@toggle-select-all="selectAllToggle"
						/>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="row in formsUsageData" :key="row.formMeta.Id">
					<td>
						<CCheckbox v-if="row.formMeta.CanManageForm" :value="row.selected.value" :aria-label="`Select ${row.formMeta.Name}`" @change="(selected)=>toggleRowSelection(row, selected)" />
					</td>
					<td class="align-middle">
						{{ row.formMeta.Name }}
						<span v-if="row.formUsageMeta.IsPaymentEnabled">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="credit-card" class="text-icon-subtle" title="Payment Enabled" />
								</template>
								<span>Payment Enabled</span>
							</CToolTip>
						</span>
						<span v-if="row.formUsageMeta.IsEncrypted">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="lock" class="text-icon-subtle" title="Encrypted" />
								</template>
								<span>Encrypted</span>
							</CToolTip>
						</span>
						<span v-if="row.formUsageMeta.IsSharedAsTemplate">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="shared-template" class="text-icon-subtle" title="Shared As Template" />
								</template>
								<span>Shared As Template</span>
							</CToolTip>
						</span>
						<span v-if="row.formUsageMeta.HasLookups">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="lookup" class="text-icon-subtle" title="Has Lookups" />
								</template>
								<span>Has Lookups</span>
							</CToolTip>
						</span>
						<span v-if="row.formUsageMeta.IsPersonSource">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="person" class="text-icon-subtle" title="People Source" />
								</template>
								<span>Referenced By Other Forms</span>
							</CToolTip>
						</span>
						<span v-if="row.formMeta.IsArchived">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="archive" class="text-icon-subtle" title="Archived" />
								</template>
								<span>Archived</span>
							</CToolTip>
						</span>
						<span v-if="row.formUsageMeta.PublicLinksDisabled">
							<CToolTip placement="top" class="cursor-default">
								<template #button>
									<Inline svg="public-link-disabled" class="text-icon-subtle" title="Public Link Disabled" />
								</template>
								<span>Public Link Disabled</span>
							</CToolTip>
						</span>
					</td>
					<td class="align-middle b6:show">{{ row.folderName }}</td>
					<td class="text-center align-middle b9:show">{{ row.formUsageMeta.DocumentTemplates.toLocaleString("en-us", { maximumFractionDigits: 0 }) }}</td>
					<td class="text-center align-middle b9:show">{{ row.formUsageMeta.StorageDisplayValue }}</td>
					<td class="text-center align-middle b7:show">
						<a
							:href="`/${globalStore.organization?.Code}/${row.formMeta.UrlName}/entries`"
							target="_blank"
						>
							{{ row.formUsageMeta.TotalEntries.toLocaleString("en-us", { maximumFractionDigits: 0 }) }}
						</a>
					</td>
					<td class="text-right">
						<CContextMenuTrigger
							v-show="!showBulkActionsBar"
							:entity-id="row.formMeta.Id"
							entity-type="form"
							location="orgSettings"
							title="Form Menu"
							aria-label="Form Menu"
						/>
					</td>
				</tr>
			</tbody>
		</table>
	</SettingsSection>
</template>

<script lang="ts">
	import { computed, defineComponent, ref, onMounted } from 'vue';
	import type { Ref, ComputedRef } from 'vue';
	import type { FormUsageViewModel } from '@cognitoforms/types/server-types/model/organization-settings/form-usage-view-model';
	import CContextMenuTrigger from 'src/components/navigation/context-menu/ContextMenuTrigger.vue';
	import { globalStore } from 'src/stores/global-store';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
	import SettingsSection from '../organization/SettingsSection.vue';
	import CCheckbox from 'src/components/form-controls/Checkbox.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import { exportFormDetails } from '@cognitoforms/api/services/form-details-service';
	import FormBulkActionsBar from './FormBulkActionsBar.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import type Table from '@cognitoforms/element-ui/packages/table/src/table.vue';

	export default defineComponent({
		components: {
			CContextMenuTrigger,
			SettingsSection,
			CCheckbox,
			CButton,
			FormBulkActionsBar,
			CToolTip
		},
		props: {
			formsUsage: { type: Array<FormUsageViewModel>, default: () => [] },
			loading: { type: Boolean, default: true },
			showOrganizationAuditLog: { type: Boolean, default: false }
		},
		emits: [ 'org-audit-log' ],
		setup(props) {
			type FormsUsageRow = { formMeta: UserFormMeta, formUsageMeta: FormUsageViewModel, folderName: string, selected: Ref<boolean> };
			const formUsageTable = ref<InstanceType<typeof Table>>();
			const formBulkActionsBar = ref();

			const sortColumn = ref(0);
			const sortAscending = ref(true);
			const nameSort = (a: FormsUsageRow, b: FormsUsageRow, ascending: boolean) => {
				const nameA = a.formMeta.Name.toLowerCase();
				const nameB = b.formMeta.Name.toLowerCase();
				const sort = (nameA < nameB) ? -1 : (nameA > nameB) ? 1 : 0;
				return ascending ? sort : -sort;
			};
			const folderSort = (a: FormsUsageRow, b: FormsUsageRow, ascending: boolean) => {
				const folderA = a.folderName?.toLowerCase();
				const folderB = b.folderName?.toLowerCase();
				// Forms with a folder always precedes forms not in a folder
				// If folders are same, always sort by ascending name
				if (folderA === undefined && folderB === undefined) return nameSort(a, b, true);
				else if (folderB === undefined) return -1;
				else if (folderA === undefined) return 1;
				const sort = (folderA < folderB) ? -1 : (folderA > folderB) ? 1 : 0;
				return sort === 0 ? nameSort(a, b, true) : ascending ? sort : -sort;
			};
			const documentTemplatesSort = (a: FormsUsageRow, b: FormsUsageRow, ascending: boolean) => {
				// If document template count is the same, always sort by ascending name
				const sort = a.formUsageMeta.DocumentTemplates - b.formUsageMeta.DocumentTemplates;
				return sort === 0 ? nameSort(a, b, true) : ascending ? sort : -sort;
			};
			const storageSort = (a: FormsUsageRow, b: FormsUsageRow, ascending: boolean) => {
				// If storage used is the same, always sort by ascending name
				const sort = a.formUsageMeta.TotalStorage - b.formUsageMeta.TotalStorage;
				return sort === 0 ? nameSort(a, b, true) : ascending ? sort : -sort;
			};
			const entryCountSort = (a: FormsUsageRow, b: FormsUsageRow, ascending: boolean) => {
				// If entry count is the same, always sort by ascending name
				const sort = a.formUsageMeta.TotalEntries - b.formUsageMeta.TotalEntries;
				return sort === 0 ? nameSort(a, b, true) : ascending ? sort : -sort;
			};
			const sorts = [nameSort, folderSort, documentTemplatesSort, storageSort, entryCountSort];
			const changeSort = (column: number) => {
				if (sortColumn.value === column) {
					sortAscending.value = !sortAscending.value;
				}
				else {
					sortColumn.value = column;
					sortAscending.value = true;
				}
			};

			const formSelectionStates = new Map();
			const formsUsageData: ComputedRef<Array<FormsUsageRow>> = computed(() =>
				props.formsUsage
					.reduce((formUsageRows, formUsageMeta) => {
						const formMeta = globalStore.forms[formUsageMeta.Id];
						if (formMeta) {
							if (!formSelectionStates.has(formMeta.Id)) {
								formSelectionStates.set(formMeta.Id, ref(false));
							}
							formUsageRows.push({
								formMeta,
								formUsageMeta,
								folderName: formMeta.FolderId ? globalStore.folders[formMeta.FolderId]?.Name : undefined,
								selected: formSelectionStates.get(formMeta.Id)
							});
						}
						return formUsageRows;
					}, [])
					.sort((a, b) => sorts[sortColumn.value](a, b, sortAscending.value))
			);

			const toggleRowSelection = (row: FormsUsageRow, selected: boolean) => { row.selected.value = selected; };
			const getSelectedForms = (): Array<UserFormMeta> => formsUsageData.value ? formsUsageData.value.filter(row => row.selected.value).map(row => row.formMeta) : [];
			const showBulkActionsBar = computed(() => formsUsageData.value.some(row => row.selected.value));
			const closeBulkActionsBar = () => {
				formsUsageData.value.forEach(row => {
					row.selected.value = false;
				});
			};
			const selectAllToggle = (val: boolean) => {
				formsUsageData.value.forEach(row => {
					if (row.formMeta.CanManageForm) row.selected.value = val;
				});
			};
			const isSelectAll = computed(() => showBulkActionsBar.value && formsUsageData.value.every(row => row.selected.value || !row.formMeta.CanManageForm));

			const downloadDetails = (forms?: FormsUsageRow[]) => {
				forms = forms ?? formsUsageData.value;
				const formIds = forms.map((x) => x.formMeta.Id);
				exportFormDetails(formIds).then((response) => {
					const blob = new Blob([response], { type: 'text/csv' });
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = 'form-details.csv';
					link.click();
				});
			};

			onMounted(() => {
				// Ensure archived forms are loaded so they can be shown in the table
				globalStore.loadArchivedFormsIfNeeded();
			});

			return {
				// refs
				formUsageTable,
				formBulkActionsBar,
				// form usage data
				sortColumn,
				sortAscending,
				changeSort,
				formsUsageData,
				// row selection
				toggleRowSelection,
				getSelectedForms,
				showBulkActionsBar,
				closeBulkActionsBar,
				selectAllToggle,
				isSelectAll,
				isSelectAllIndeterminate: computed(() => showBulkActionsBar.value && !isSelectAll.value),
				// misc
				globalStore: globalStore,
				downloadDetails
			};
		}
	});
</script>

<style lang="scss">
.org-settings-form-usage {
	@media (min-width: 1350px) {
		padding-bottom: $space__5;
	}

	&__checkbox {
		top: -1px;
	}

	thead {
		top: -1px;
		z-index: $z1;

		th {
			vertical-align: middle;
			line-height: 1.2;

			> span,
			> div {
				height: 40px;
			}
		}

		.header-row {
			border-bottom: 0;
		}

		.bulk-actions-row {
			border-right: 0;
			border-bottom: 0;
			border-left: 0;
		}
	}

	tbody tr {
		height: 50px;

		td {
			vertical-align: middle;
		}
	}

	td {
		overflow-wrap: anywhere;
	}

	.col-checkbox {
		width: 32px;
	}

	.col-actions {
		width: 50px;
	}
}

// :has is deprecated by Cognito Forms
// stylelint-disable-next-line selector-pseudo-class-disallowed-list
.not-iframe-page:has(.org-settings-form-usage) .admin--desktop {
	overflow: hidden;
}
</style>